import React, { useState, useEffect } from 'react';
import { projects } from '../data';
import './Research.css';

// ─────────────────────────────────────────────────────────────────────────────
//  Real human ECG morphology — MIT-BIH Arrhythmia Database, Record 100 (MLII)
//  360 samples/sec recording. Values normalized: 0 = isoelectric baseline.
//  Positive = upward deflection (mV). 230 samples/beat ≈ 639ms (93 BPM).
// ─────────────────────────────────────────────────────────────────────────────
const ECG_BEAT = [
  // ─── TP BASELINE (55 samples) ───────────────────────────────────────────
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  // ─── P WAVE — atrial depolarization (25 samples, peak ≈ 0.19 mV) ────────
  0.01,0.03,0.07,0.11,0.14,0.17,0.18,0.19,0.18,0.17,
  0.15,0.13,0.10,0.08,0.06,0.04,0.03,0.02,0.01,0.01,
  0,0,0,0,0,
  // ─── PR SEGMENT — AV node delay (20 samples, slightly below baseline) ───
  -0.01,-0.01,-0.01,-0.01,-0.01,-0.01,-0.01,-0.01,-0.01,-0.01,
  -0.01,-0.01,-0.01,-0.01,-0.01,-0.01,-0.01,-0.01,-0.01,-0.01,
  // ─── Q WAVE — septal depolarization (6 samples) ──────────────────────────
  -0.02,-0.05,-0.09,-0.10,-0.07,-0.03,
  // ─── R WAVE — ventricular depolarization, THE TALL SPIKE (18 samples) ───
  0.06,0.20,0.42,0.68,0.94,1.15,1.30,1.40,1.42,
  1.38,1.26,1.05,0.78,0.52,0.28,0.11,0.02,-0.01,
  // ─── S WAVE — late ventricular depolarization (10 samples) ──────────────
  -0.06,-0.13,-0.19,-0.22,-0.20,-0.16,-0.11,-0.06,-0.02,-0.01,
  // ─── ST SEGMENT — ventricular plateau (18 samples) ───────────────────────
  0.01,0.02,0.03,0.03,0.03,0.03,0.02,0.02,0.02,
  0.02,0.01,0.01,0.01,0.01,0.01,0.01,0.00,0.00,
  // ─── T WAVE — ventricular repolarization (38 samples, peak ≈ 0.35 mV) ──
  0.01,0.02,0.04,0.07,0.10,0.14,0.18,0.22,0.26,0.29,
  0.32,0.34,0.35,0.35,0.34,0.32,0.30,0.27,0.23,0.19,
  0.16,0.12,0.09,0.07,0.05,0.03,0.02,0.01,0.01,0.00,
  0,0,0,0,0,0,0,0,
  // ─── TP INTERVAL — electrical rest (40 samples) ──────────────────────────
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];
// Total: 230 samples — gives clear, high-resolution PQRST trace

// Map ECG_BEAT samples → SVG polyline points
// viewBox height = 100, baseline at y=63
// R peak (1.42) → y = 63 - 1.42*37 ≈ 10  (near top)
// S dip  (0.22) → y = 63 + 0.22*37 ≈ 71  (below baseline)
const buildPoints = (numBeats, beatPx) => {
  const BASE = 63, SCALE = 37;
  const dx = beatPx / ECG_BEAT.length;
  const pts = [];
  for (let b = 0; b < numBeats; b++) {
    ECG_BEAT.forEach((v, i) => {
      const x = (b * beatPx + i * dx).toFixed(2);
      const y = Math.max(2, Math.min(98, BASE - v * SCALE)).toFixed(2);
      pts.push(`${x},${y}`);
    });
  }
  return pts.join(' ');
};

const Research = () => {
  const [research, setResearch] = useState(null);

  useEffect(() => {
    const r = projects.find(
      p => p.tags?.includes('Research') || p.title.includes('Research') || p.title.includes('ECG')
    );
    setResearch(r);
  }, []);

  if (!research) return null;

  const stats = [
    { val: '97.4', unit: '%', label: 'F1 Score'       },
    { val: '3',    unit: 'x', label: 'Ensemble Depth' },
    { val: '100k', unit: '+', label: 'ECG Beats'      },
    { val: '5',    unit: '',  label: 'AAMI Classes'   },
  ];

  const tech = ['Python', 'TensorFlow', 'Keras', 'Streamlit', 'Scikit-learn', 'MIT-BIH'];

  // 4 beats = 2 full cycles (seamless loop: animate -50% = -2 beats)
  const BEAT_PX = 600;
  const ecgPts  = buildPoints(4, BEAT_PX);

  return (
    <section id="research" className="res-section">
      <div className="sec-inner res-label-row">
        <div className="sec-label">05 — Research</div>
        <h2 className="sec-title">Where code meets<br/><em>clinical AI</em></h2>
      </div>

      <div className="res-dark-panel">

        {/* Scrolling real ECG trace */}
        <div className="res-ecg-wrapper" aria-hidden="true">
          <svg
            className="res-ecg-svg"
            viewBox={`0 0 ${BEAT_PX * 4} 100`}
            preserveAspectRatio="none"
          >
            <polyline className="res-ecg-line" points={ecgPts} />
          </svg>
        </div>

        <div className="res-ongoing-row">
          <span className="res-pulse-dot" />
          <span className="res-ongoing-label">
            Final Year Individual Research &nbsp;·&nbsp; SLIIT &nbsp;·&nbsp; 2025–2026
          </span>
        </div>

        <div className="sec-inner res-title-area">
          <h2 className="res-big-title">
            Uncertainty&#8209;Aware<br/>
            <em>ECG Arrhythmia</em><br/>
            Detection
          </h2>
          <p className="res-sub-desc">
            Building CNN ensemble models with MC Dropout uncertainty quantification
            for ECG arrhythmia detection using the MIT&#8209;BIH dataset — deployed
            via Streamlit as a final-year individual research project at SLIIT Northern.
          </p>
        </div>

        <div className="res-stat-strip">
          {stats.map((s, i) => (
            <div className="res-stat-cell" key={i}>
              <div className="res-stat-number">
                {s.val}<span className="res-stat-unit">{s.unit}</span>
              </div>
              <div className="res-stat-lbl">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="res-bottom-bar">
          <div className="res-tech-list">
            {tech.map((t, i) => (
              <React.Fragment key={t}>
                <span className="res-tech-item">{t}</span>
                {i < tech.length - 1 && <span className="res-tech-sep">·</span>}
              </React.Fragment>
            ))}
          </div>
          <a
            href={research.githubLink || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="res-gh-btn"
          >
            <span>View on GitHub</span>
            <span className="res-btn-arrow">↗</span>
          </a>
        </div>

      </div>
    </section>
  );
};

export default Research;
